import { Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import * as Utilities from "../../data/utilities";
import { FinancialsMetrics } from "../../types/Financials";
import { BALANCE_SHEET_ROWS, getOtherTags, MetricRow, PROFIT_AND_LOSS_ROWS } from "../../data/financialsSchema";
import { DELTA_PRIMARY, TEXT_SECONDARY } from "../colors";

// Shared with FinancialsPanel, which links to these anchors from the top of
// the tab so the statements are discoverable without scrolling first.
export const FINANCIALS_SECTION_IDS = {
  balanceSheet: "financials-balance-sheet",
  profitAndLoss: "financials-profit-and-loss",
  other: "financials-other",
};

const NUMBER_FORMAT = new Intl.NumberFormat("en-GB", { maximumFractionDigits: 0 });

// Accounting convention: negatives in brackets rather than a minus sign.
const formatValue = (value: number | undefined): string => {
  if (value === undefined) return "—";
  const formatted = NUMBER_FORMAT.format(Math.abs(value));
  return value < 0 ? `(${formatted})` : formatted;
};

// XBRL tag names are raw PascalCase, e.g. "TotalAssetsLessCurrentLiabilities" -
// split before each capital that follows a lowercase letter to make them readable.
const formatMetricName = (name: string): string => name.replace(/([a-z])([A-Z])/g, "$1 $2");

const resolveRow = (
  row: MetricRow,
  periods: string[],
  accounts: Record<string, FinancialsMetrics>
): Record<string, number | undefined> => {
  const values: Record<string, number | undefined> = {};
  for (const period of periods) {
    for (const key of row.aliases) {
      if (accounts[period][key] !== undefined) {
        values[period] = accounts[period][key];
        break;
      }
    }
  }
  return values;
};

const Statement = (props: {
  id: string;
  title: string;
  rows: MetricRow[];
  periods: string[];
  accounts: Record<string, FinancialsMetrics>;
}) => {
  const { id, title, rows, periods, accounts } = props;

  const resolvedRows = rows
    .map((row) => ({ row, values: resolveRow(row, periods, accounts) }))
    .filter(({ values }) => Object.keys(values).length > 0);

  if (resolvedRows.length === 0) {
    return null;
  }

  return (
    <Box id={id} style={{ marginBottom: 24, scrollMarginTop: 16 }}>
      <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700, padding: "0 10px 8px" }}>
        {title}
      </Typography>
      <TableContainer style={{ overflowX: "auto", WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell style={{ fontWeight: 700, whiteSpace: "nowrap" }}>Line Item</TableCell>
              {periods.map((period) => (
                <TableCell key={period} align="right" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {Utilities.getDateDisplay(period)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {resolvedRows.map(({ row, values }) => (
              <TableRow key={row.label}>
                <TableCell
                  style={{
                    whiteSpace: "nowrap",
                    color: row.subtotal ? undefined : TEXT_SECONDARY,
                    fontWeight: row.subtotal ? 700 : 400,
                    borderTop: row.subtotal ? "1px solid #E2E2E7" : undefined,
                  }}
                >
                  {row.label}
                </TableCell>
                {periods.map((period) => (
                  <TableCell
                    key={period}
                    align="right"
                    style={{
                      fontWeight: row.subtotal ? 700 : 400,
                      borderTop: row.subtotal ? "1px solid #E2E2E7" : undefined,
                    }}
                  >
                    {formatValue(values[period])}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

// Balance Sheet and P&L follow the line-item order set out in the Companies
// Act 2006 Format 1 (SI 2008/410, Schedule 1) - see financialsSchema.ts for
// how real XBRL tags map onto those statutory line items. Anything a company
// reports that isn't covered by that mapping still shows up below, in a
// flat "Other" section, rather than being silently dropped.
export const FinancialsTable = (props: { accounts: Record<string, FinancialsMetrics> }) => {
  const { accounts } = props;
  const periods = Object.keys(accounts).sort().reverse(); // most recent first
  const otherTags = getOtherTags(accounts);

  return (
    <Box style={{ padding: 10 }}>
      <Statement
        id={FINANCIALS_SECTION_IDS.balanceSheet}
        title="Balance Sheet"
        rows={BALANCE_SHEET_ROWS}
        periods={periods}
        accounts={accounts}
      />
      <Statement
        id={FINANCIALS_SECTION_IDS.profitAndLoss}
        title="Profit & Loss"
        rows={PROFIT_AND_LOSS_ROWS}
        periods={periods}
        accounts={accounts}
      />

      {otherTags.length > 0 && (
        <Box id={FINANCIALS_SECTION_IDS.other} style={{ scrollMarginTop: 16 }}>
          <Typography variant="subtitle1" style={{ color: DELTA_PRIMARY, fontWeight: 700, padding: "0 10px 8px" }}>
            Other
          </Typography>
          <TableContainer style={{ overflowX: "auto", WebkitTextSizeAdjust: "100%", textSizeAdjust: "100%" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700, whiteSpace: "nowrap" }}>Line Item</TableCell>
                  {periods.map((period) => (
                    <TableCell key={period} align="right" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                      {Utilities.getDateDisplay(period)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {otherTags.map((tag) => (
                  <TableRow key={tag}>
                    <TableCell style={{ color: TEXT_SECONDARY, whiteSpace: "nowrap" }}>
                      {formatMetricName(tag)}
                    </TableCell>
                    {periods.map((period) => (
                      <TableCell key={period} align="right">
                        {formatValue(accounts[period][tag])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default FinancialsTable;
